/**
 * D17 — Profile & Settings.
 *
 * Adds the anonymous-by-default preference the artwork omitted (gap G-02).
 * The "Distribution Photos" toggle here is an account *default*: it seeds new
 * donations, and does not retroactively change consent already given or
 * withheld on a past donation (gap G-18).
 */

import { StyleSheet, View } from 'react-native';

import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ErrorState } from '../../components/feedback/States';
import { GlyphCircle } from '../../components/common/Icon';
import { ListRow } from '../../components/common/ListRow';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { Toggle } from '../../components/forms/Choice';
import { useAuthStore } from '../../core/auth/useAuth';
import { useMyProfile, useUpdatePreferences } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function ProfileScreen({ navigation }: DonorScreenProps<'Profile'>) {
  const t = useT();
  const query = useMyProfile();
  const signOut = useAuthStore((s) => s.signedOut);
  const updatePreferences = useUpdatePreferences();

  const profile = query.data;
  const preferences = profile?.preferences;

  const setPreference = (key: string, value: boolean) =>
    updatePreferences.mutate({ [key]: value });

  return (
    <ScreenShell
      leading="menu"
      onMenuPress={() => navigation.getParent()?.dispatch({ type: 'OPEN_DRAWER' })}
      testID="screen-profile"
    >
      {query.isError ? (
        <ErrorState
          title={t('Profile unavailable')}
          message={t('Sign in to view and manage your account.')}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <View style={styles.profileCard}>
            <GlyphCircle name="person" size={64} />
            <View style={styles.profileText}>
              {query.isLoading || !profile ? (
                <>
                  <Skeleton width={140} height={20} />
                  <Skeleton width={180} height={14} />
                </>
              ) : (
                <>
                  <Text variant="cardTitle" tone="onBrand">
                    {profile.fullName}
                  </Text>
                  {profile.email ? (
                    <Text variant="caption" tone="onBrandMuted">
                      {profile.email}
                    </Text>
                  ) : null}
                  <Text variant="caption" tone="onBrandMuted">
                    {profile.mobileNumber}
                  </Text>
                </>
              )}
            </View>
          </View>

          <Text variant="sectionTitle" style={styles.heading}>{t('Account')}</Text>
          <Card>
            <ListRow title={t('Personal Information')} icon="person" onPress={() => {}} testID="row-personal" />
            <ListRow title={t('Change Password')} icon="privacy" onPress={() => {}} testID="row-password" />
            <ListRow title={t('Payment Methods')} icon="card" onPress={() => {}} testID="row-payment-methods" />
          </Card>

          <Text variant="sectionTitle" style={styles.heading}>{t('Donation Preferences')}</Text>
          <Card>
            <Toggle
              testID="toggle-updates"
              label={t('Donation Updates')}
              value={preferences?.donationUpdates ?? true}
              onChange={(value) => setPreference('donationUpdates', value)}
            />
            <Toggle
              testID="toggle-completion"
              label={t('Completion Notifications')}
              value={preferences?.completionNotifications ?? true}
              onChange={(value) => setPreference('completionNotifications', value)}
            />
            <Toggle
              testID="toggle-photos"
              label={t('Distribution Photos')}
              description={t('Default for new donations. Past donations keep the choice you made then.')}
              value={preferences?.distributionPhotos ?? true}
              onChange={(value) => setPreference('distributionPhotos', value)}
            />
            <Toggle
              testID="toggle-anonymous"
              label={t('Donate Anonymously')}
              description={t('Your name will not be shown on new donations.')}
              value={preferences?.anonymousByDefault ?? false}
              onChange={(value) => setPreference('anonymousByDefault', value)}
            />
          </Card>

          <Text variant="sectionTitle" style={styles.heading}>{t('Support & Legal')}</Text>
          <Card>
            <ListRow
              title={t('Help & Support')}
              icon="help"
              onPress={() => navigation.navigate('Help')}
              testID="row-help"
            />
            <ListRow title={t('Terms & Conditions')} icon="donations" onPress={() => {}} testID="row-terms" />
            <ListRow title={t('Privacy Policy')} icon="privacy" onPress={() => {}} testID="row-privacy" />
          </Card>

          <Button
            label={t('Logout')}
            variant="danger"
            style={styles.logout}
            testID="cta-logout"
            onPress={() => {
              void signOut();
              navigation.navigate('Home');
            }}
          />
        </>
      )}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  profileCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.lg,
    padding: space.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.brand,
  },
  profileText: {
    flex: 1,
    gap: space.xxs,
  },
  heading: {
    marginTop: space.xl,
    marginBottom: space.md,
  },
  logout: {
    marginTop: space.xl,
  },
});
